import React from 'react';
import { X, Globe, Key, Sun, Moon, Presentation, Download } from 'lucide-react';
import { TRANSLATIONS } from '../constants';
import { LanguageSelector } from './LanguageSelector';
import { ApiSettings } from './ApiSettings';
import { ApiSettings as ApiSettingsType } from '../types';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  language: 'ko' | 'en';
  isDarkMode: boolean;
  onDarkModeToggle: () => void;
  onLanguageChange: (lang: 'ko' | 'en') => void;
  apiSettings: ApiSettingsType;
  onApiSettingsChange: (settings: ApiSettingsType) => void;
  onPresentationClick: () => void;
  onExportClick: () => void;
  hasSlides: boolean;
  onApiKeySave?: (apiKey: string) => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  language,
  isDarkMode,
  onDarkModeToggle,
  onLanguageChange,
  apiSettings,
  onApiSettingsChange,
  onPresentationClick,
  onExportClick,
  hasSlides,
  onApiKeySave
}) => {
  if (!isOpen) {return null;}

  const t = TRANSLATIONS[language];

  const handlePresentationClick = () => {
    onClose();
    onPresentationClick();
  };

  const handleExportClick = () => {
    onClose();
    onExportClick();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={`relative w-full max-w-2xl mx-4 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      } rounded-lg shadow-xl`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h2 className={`text-xl font-semibold ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {t.settings || '설정'}
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode 
                ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' 
                : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Language Setting */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Globe className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
              <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {language === 'ko' ? '언어 설정' : 'Language Settings'}
              </h3>
            </div>
            <LanguageSelector 
              currentLanguage={language}
              onLanguageChange={onLanguageChange}
              isDarkMode={isDarkMode}
            />
          </div>

          {/* API Settings */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Key className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
              <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {language === 'ko' ? 'API 설정' : 'API Settings'}
              </h3>
            </div>
            <ApiSettings
              apiSettings={apiSettings}
              onApiKeyChange={(apiKey) => {
                onApiSettingsChange({
                  geminiApiKey: apiKey,
                  isConfigured: !!apiKey
                });
                if (onApiKeySave) {
                  onApiKeySave(apiKey);
                }
              }}
              language={language}
              isDarkMode={isDarkMode}
            />
          </div>

          {/* Dark Mode */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              {isDarkMode ? (
                <Moon className="w-5 h-5 text-gray-400" />
              ) : (
                <Sun className="w-5 h-5 text-gray-600" />
              )}
              <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {language === 'ko' ? '다크 모드' : 'Dark Mode'}
              </h3>
            </div>
            <button
              onClick={onDarkModeToggle}
              className={`flex items-center justify-between w-full p-3 rounded-lg border transition-colors ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' 
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                {isDarkMode 
                  ? (language === 'ko' ? '다크 모드 사용 중' : 'Dark Mode Enabled')
                  : (language === 'ko' ? '라이트 모드 사용 중' : 'Light Mode Enabled')
                }
              </span>
              <div className={`w-12 h-6 rounded-full transition-colors ${
                isDarkMode ? 'bg-blue-600' : 'bg-gray-300'
              }`}>
                <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                  isDarkMode ? 'translate-x-6' : 'translate-x-0.5'
                }`} style={{ marginTop: '0.125rem' }} />
              </div>
            </button>
          </div>

          {/* Presentation & Export */}
          {hasSlides && (
            <div>
              <h3 className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {language === 'ko' ? '프레젠테이션' : 'Presentation'}
              </h3>
              <div className="flex gap-3">
                <button
                  onClick={handlePresentationClick}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <Presentation className="w-5 h-5" />
                  {language === 'ko' ? '프레젠테이션 보기' : 'Presentation View'}
                </button>
                <button
                  onClick={handleExportClick}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <Download className="w-5 h-5" />
                  {language === 'ko' ? '내보내기' : 'Export'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};